import { useCallback, type MouseEvent } from 'react';
import { useRouter } from 'next/router';
import { useBasePath } from './use-base-path';
import { useLeaveChatOnNavigate } from './use-leave-chat';

/**
 * The bare click-modifier fields `routerHandlesClick` needs — not `MouseEvent<T>`, so a caller shared
 * across an anchor AND a native `<button>` (different `T`) can use one type instead of two, and so a
 * programmatic call with no real event can pass `{}` (an absent field reads as "not held").
 */
export interface ClickModifiers {
  defaultPrevented?: boolean;
  metaKey?: boolean;
  ctrlKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
  button?: number;
}

/**
 * Whether the ROUTER should take this click, or the browser should.
 *
 * A modified or non-primary click is the user asking for a new tab / window / download, and an
 * already-prevented one belongs to whatever prevented it. Pure so the rule is testable without a
 * DOM — the whole point of keeping these rows anchors is that these clicks still work.
 */
export function routerHandlesClick(event: ClickModifiers): boolean {
  if (event.defaultPrevented) return false;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
  return (event.button ?? 0) === 0;
}

/**
 * An in-app destination rendered as a real `<a href>` that navigates through the ROUTER.
 *
 * A bare `<a href="/packs">` inside the app is a full page LOAD, and a load throws the whole
 * persistent frame away — including the chat. The chat is a companion overlay that rides along
 * across navigations (`useLeaveChatOnNavigate`), and its size + thread survive a refresh only
 * because `useChatUrlSync` keeps them in `?chat=`/`?conversation=` on the URL you are ON; a hard
 * hop to a bare `${basePath}/packs` carries neither, so the chat came back collapsed and the
 * thread went quiet. Every other in-app link in the app is a `next/link` or a `router.push` for
 * this reason — these rows were the exception (#Account → Symphony Packs / Connectors / Agent
 * models).
 *
 * Still an anchor, not a button: cmd/ctrl/shift/alt-click, middle-click and "copy link address"
 * must keep working, so the href stays real and only a plain left-click is intercepted.
 *
 * `route` is the Next route (no mount prefix) — the hook returns the mount-prefixed `href` from
 * the same string, so the two can't drift apart.
 *
 * @param onNavigate fired on every click (BI belongs here — a cmd-click is a click too), before
 *                   the navigation.
 */
export function useInAppLink(
  route: string,
  onNavigate?: () => void,
): { href: string; onClick: (event: MouseEvent<HTMLElement>) => void } {
  const router = useRouter();
  const basePath = useBasePath();
  const leaveChat = useLeaveChatOnNavigate();
  const href = `${basePath}${route}`;
  const onClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      onNavigate?.();
      // Leave a modified / non-primary click to the browser — that is the new tab or window the
      // user asked for, and it is why this is still an anchor.
      if (!routerHandlesClick(event)) return;
      event.preventDefault();
      // The same adjustment every other nav click makes: a maximized chat floats down beside the
      // page being opened (on narrow, where it would cover it, it collapses to the bubble).
      leaveChat();
      void router.push(route, href);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [router, route, href, leaveChat, onNavigate],
  );
  return { href, onClick };
}
